import { isMobilityPulseAdsEnabled } from '@client/adManager/AdUnit/utils/isMobilityPulseAdsEnabled.js';
import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import { isBannerOverlappingFilters } from '@client/core/utils/dom/handleAdsOverlap/filterCollisionDetection.js';
import { WallpaperHandler } from '@client/core/utils/dom/WallpaperHandler.js';
import { handleSeenThisSlotRender } from '@client/core/utils/SeenThis/SeenThis.js';
import {
  AdPlacement,
  type AdType,
  debugLog,
  DisplayPlacementId,
  GamPlacementDataStatus,
  type PlacementId,
  PlacementStatus
} from '@schibsted-nmp/advertising-shared';

import {
  eventIsNativeCpmAd,
  sizeIsAdnami,
  sizeIsFullScreenPortrait,
  sizeIsFullScreenLandscape,
  sizeIsFullScreenAd,
  adTypeIsTakeover,
  sizeIsNativeAd,
  sizeIsDbaDesktopIntermingle,
  sizeExceedsCenterContentWidth,
  sizeBlocketTop1Wallpaper,
  isPostItTestCreative
} from '../util.js';

/** Fired when creative code has been injected into an ad slot. */
export function onSlotRenderEnded(
  event: googletag.events.SlotRenderEndedEvent
) {
  const placementId = event.slot.getSlotElementId() as PlacementId;

  if (event.isEmpty) {
    onSlotRenderEnded.whenEmpty(placementId, event);
    return;
  }

  if (isMobilityPulseAdsEnabled()) {
    updateMetricByKey(placementId, GamPlacementDataStatus.GamRenderNonEmpty);

    const missingByStatus = new Map<GamPlacementDataStatus, boolean>([
      [GamPlacementDataStatus.GamLineItemIdMissing, event.lineItemId == null],
      [GamPlacementDataStatus.GamCreativeIdMissing, event.creativeId == null],
      [GamPlacementDataStatus.GamCampaignIdMissing, event.campaignId == null],
      [
        GamPlacementDataStatus.GamAdvertiserIdMissing,
        event.advertiserId == null
      ],
      [
        GamPlacementDataStatus.GamSourceAgnosticLineItemIdMissing,
        event.sourceAgnosticLineItemId == null
      ],
      [
        GamPlacementDataStatus.GamSourceAgnosticCreativeIdMissing,
        event.sourceAgnosticCreativeId == null
      ]
    ]);

    for (const [status, isMissing] of missingByStatus) {
      if (isMissing) updateMetricByKey(placementId, status);
    }
  }

  const isMiddleSlot = placementId.startsWith('advt_middle_');
  const isBottomSlot = placementId === DisplayPlacementId.AdvtBottom1;

  if (
    (isMiddleSlot || isBottomSlot) &&
    onSlotRenderEnded.shouldCollapseAd(event.size, placementId)
  ) {
    onSlotRenderEnded.whenAdCollided(placementId, event);
    return;
  }

  const creativeType = onSlotRenderEnded.getCreativeType(
    event,
    isMiddleSlot,
    placementId
  );

  const isTakeover = adTypeIsTakeover(creativeType);

  if (isTakeover && placementId === AdPlacement.AdvtMiddle4.id) {
    handleSeenThisSlotRender();
  }

  if (creativeType === 'adnami' && placementId === AdPlacement.AdvtMiddle2.id) {
    WallpaperHandler.setupAdnamiMidscrollObserver();
  }

  debugLog(
    `Creative type from size ${JSON.stringify(event.size)}: ${creativeType}`
  );

  updatePlacementKeyValueById(placementId, 'creativeType', creativeType);
  updatePlacementKeyValueById(placementId, 'status', 'loaded');
  updateMetricByKey(placementId, PlacementStatus.AdLoaded);
}

export namespace onSlotRenderEnded {
  export function whenEmpty(
    placementId: PlacementId,
    event: googletag.events.SlotRenderEndedEvent
  ) {
    debugLog(
      `Google ad did not fill for placement: ${placementId} with slot`,
      event.slot
    );

    updateMetricByKey(placementId, PlacementStatus.AdNoFill);
    updatePlacementKeyValueById(placementId, 'status', 'error');
  }

  export function whenAdCollided(
    placementId: PlacementId,
    event: googletag.events.SlotRenderEndedEvent
  ) {
    debugLog(
      `Google ad overlaps with search filters: ${placementId} with slot`,
      event.slot
    );

    updateMetricByKey(placementId, PlacementStatus.CollapsedOnCollision);

    updatePlacementKeyValueById(placementId, 'status', 'collapsedOnCollision');
  }

  export function getCreativeType(
    event: googletag.events.SlotRenderEndedEvent,
    isMiddleSlot: boolean,
    placementId: PlacementId
  ): AdType {
    const { size } = event;

    if (
      (placementId === DisplayPlacementId.AdvtTop1 ||
        placementId === DisplayPlacementId.AdvtTop1Premium) &&
      sizeBlocketTop1Wallpaper(size)
    ) {
      return 'top1-wallpaper';
    }

    if (placementId === AdPlacement.AdvtAudience.id) return 'audience-pixel';
    // Check ad types in order of specificity
    if (isMiddleSlot && sizeIsDbaDesktopIntermingle(size)) {
      return 'intermingles';
    }
    if (sizeIsAdnami(size)) return 'adnami';
    if (sizeIsFullScreenPortrait(size)) return 'takeover-portrait';
    if (sizeIsFullScreenLandscape(size)) return 'takeover-landscape';
    if (sizeIsFullScreenAd(size)) return 'takeover';
    if (eventIsNativeCpmAd(event)) return 'sponsored-content';
    if (sizeIsNativeAd(size)) return 'native';
    if (isPostItTestCreative(event)) return 'postit';

    return 'banner';
  }

  export function shouldCollapseAd(
    adSize: googletag.events.SlotRenderEndedEvent['size'],
    placementId: PlacementId
  ): boolean {
    return (
      sizeExceedsCenterContentWidth(adSize) &&
      isBannerOverlappingFilters(placementId)
    );
  }
}
